import {
  LOCATION_PIN_ACTIVE_OPACITY,
  LOCATION_PIN_DEFAULT_OPACITY,
  LOCATION_PIN_METERS_PER_PIXEL,
} from '../map.constants';

type LocationPinVisibilityInput = {
  isReadonly: boolean;
  isDesignEmpty: boolean;
  metersPerPixel: number;
};

export type LocationPinOverlayFlags = {
  showBackgroundMap: boolean;
  suppressBlueprint: boolean;
};

type LocationPinOverlayInput = {
  locationPinMode: boolean;
  showBackgroundMap: boolean;
  measureIsolationActive: boolean;
};

export type LocationPinOverlayTarget = {
  setDisplaySuppressed: (suppressed: boolean) => void;
  setBackgroundMapVisible?: (show: boolean) => void;
};

type BuildingGfaLabelVisibilityInput = {
  overlayVisible: boolean;
  isLocationPinVisible: boolean;
};

type LocationPinAppearanceInput = {
  hover: boolean;
  dragging: boolean;
};

/**
 * True when building overlays are too small to read. The location pin then
 * replaces them; it must stay off whenever buildings are still drawn.
 */
export const shouldShowLocationPin = ({
  isReadonly,
  isDesignEmpty,
  metersPerPixel,
}: LocationPinVisibilityInput): boolean => {
  if (isReadonly) return false;
  if (isDesignEmpty) return false;
  return metersPerPixel >= LOCATION_PIN_METERS_PER_PIXEL;
};

/**
 * Overlay visibility while the location pin is shown.
 * Forces the basemap on and hides PDF, unless measure isolation is hiding
 * the map to calibrate a sheet.
 * @returns {@link LocationPinOverlayFlags} for the current pin / Show / measure state
 */
export const resolveLocationPinOverlayFlags = ({
  locationPinMode,
  showBackgroundMap,
  measureIsolationActive,
}: LocationPinOverlayInput): LocationPinOverlayFlags => {
  if (measureIsolationActive) {
    return { showBackgroundMap: false, suppressBlueprint: false };
  }
  if (locationPinMode) {
    return { showBackgroundMap: true, suppressBlueprint: true };
  }
  return { showBackgroundMap, suppressBlueprint: false };
};

/**
 * Push {@link LocationPinOverlayFlags} to the map in one step so PDF suppress
 * and the basemap cannot drift. Omitting
 * {@link LocationPinOverlayTarget.setBackgroundMapVisible} still updates PDF
 * when the Mapbox instance is not attached yet.
 */
export const applyLocationPinOverlayFlags = (
  flags: LocationPinOverlayFlags,
  target: LocationPinOverlayTarget,
): void => {
  target.setDisplaySuppressed(flags.suppressBlueprint);
  target.setBackgroundMapVisible?.(flags.showBackgroundMap);
};

/**
 * GFA labels sit on building overlays. Hide them while the location pin
 * replaces those overlays.
 */
export const shouldShowBuildingGfaLabel = ({
  overlayVisible,
  isLocationPinVisible,
}: BuildingGfaLabelVisibilityInput): boolean =>
  overlayVisible && !isLocationPinVisible;

/** Keep the pin subdued until it is the active pointer target. */
export const getLocationPinOpacity = ({
  hover,
  dragging,
}: LocationPinAppearanceInput): number =>
  hover || dragging
    ? LOCATION_PIN_ACTIVE_OPACITY
    : LOCATION_PIN_DEFAULT_OPACITY;
